import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "@emotion/styled";
import moment from "moment";
import _ from "lodash";

import {
  selectWorkOrdersAssignees,
  selectWorkOrdersCreativePrimes,
  selectWorkOrdersResultCount,
  selectWorkOrdersResults,
  workOrderBriefsLoadingSelector,
} from "../../redux/selectors/workOrders";
import {
  getExportWorkOrderListUrl,
  getWorkOrderFilters,
  getWorkOrderList,
  resetWorkOrder,
} from "../../redux/actions/workOrderActions";
import { TWorkOrderResults } from "../../redux/reducers/workOrdersReducer";
import { BriefType } from "../../constants/types";
import { RESULTS_PER_PAGE } from "../../utils/constants";
import { useURLQueryString } from "../../hooks/useUrlQueryString";
import debounce from "../../utils/debounce";
import { WorkOrderProps } from "../../features/brief-list/types/props/work-order.props";
import { FilterChips } from "../../components/filters/components/filter-chips/filter-chips.components";
import { ListingTable } from "../../components/layout/listing-table/listing-table.component";
import { TRow } from "../../components/layout/listing-table/listing-table.types";
import { getResourceLink } from "../../features/navigation/types/pages.functions";
import { BasicResourceType } from "../../types/resources/resources.enums";
import { SpinnerLoader } from "../../components/loaders";
import { Pagination } from "../../components/Pagination/pagination.component";
import { getTotalPages } from "../../components/Pagination/pagination.functions";
import { FileDownload } from "../../features/file-attachments/file-download/components/file-download/file-download-component";

import { workOrderListingColumns } from "./work-order-list.configuration";

const Wrapper = styled.div`
  width: 100%;
  text-align: left;
  padding: 20px;
`;

export const WorkOrderList = () => {
  const dispatch = useDispatch();
  const {
    values: { limit = [RESULTS_PER_PAGE], page = [1], ...parameters },
  } = useURLQueryString();
  const limitRef = useRef(limit);
  const pageRef = useRef(page);
  const paramsRef = useRef(parameters ?? {});
  const assignees = useSelector(selectWorkOrdersAssignees) ?? [];
  const creativePrimes = useSelector(selectWorkOrdersCreativePrimes) ?? [];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(
    debounce((params) => dispatch(getWorkOrderList(params)), 1000),
    []
  );

  useEffect(() => {
    if (!_.isEqual(paramsRef.current, parameters)) {
      if (_.isEqual(paramsRef.current, {}) || _.isEqual(parameters, {})) {
        dispatch(getWorkOrderList(parameters));
      } else {
        debouncedFetch(parameters);
      }
      paramsRef.current = parameters;
    } else if (!_.isEqual([limitRef.current, pageRef.current], [limit, page])) {
      limitRef.current = limit;
      pageRef.current = page;
      dispatch(getWorkOrderList({ limit, page, ...paramsRef.current }, true));
    }
  }, [debouncedFetch, dispatch, limit, page, parameters]);

  useEffect(() => {
    // get all work orders briefs
    dispatch(getWorkOrderFilters());
    dispatch(getWorkOrderList({ limit, page, ...parameters }));

    return () => {
      dispatch(resetWorkOrder());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const workOrderListResult = useSelector(selectWorkOrdersResults);
  const resultCount = useSelector(selectWorkOrdersResultCount);
  const loading = useSelector(workOrderBriefsLoadingSelector);

  const convertData = (origData?: TWorkOrderResults[]): WorkOrderProps[] => {
    if (origData) {
      return origData.map((item) => ({
        ...item,
        createdDate: moment(item.createdAt).format("YYYY-MM-DD"),
        dueDate: item.dueAt ? moment(item.dueAt).format("MMM DD") : "-",
      }));
    } else {
      return [];
    }
  };

  const dataList = convertData(workOrderListResult);

  const getLink = (workOrder: TRow) =>
    getResourceLink(workOrder.id, BasicResourceType.WorkOrder) ?? "";

  return (
    <Wrapper>
      <FilterChips
        filterType={BriefType.WORK_ORDER}
        optionLists={{ users: assignees, primes: creativePrimes }}
      >
        <FileDownload
          url={getExportWorkOrderListUrl({ limit, page, ...parameters })}
          accept="text/csv"
          authRequired
          saveAsFileName="work-orders.csv"
          variant="simple-button"
        >
          Export list
        </FileDownload>
      </FilterChips>
      <SpinnerLoader showIf={loading.inProgress} size={40} />
      <ListingTable columns={workOrderListingColumns} dataList={dataList} link={getLink} />
      <Pagination count={getTotalPages(resultCount)} />
    </Wrapper>
  );
};
